import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { type IntlShape } from 'react-intl-next';
import { z } from 'zod';

import {
  executeObjectResolverAction,
  type ExecuteActionRequest,
} from '@canvas/atlassian-service/datasource';

interface JiraPriorityEntity {
  id: string;
  text?: string;
  label?: string;
  source?: string;
  description?: string;
}

export const PriorityItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  iconUrl: z.string().optional(),
  description: z.string().optional(),
});

export type PriorityItem = z.infer<typeof PriorityItemSchema>;

export interface PriorityItemValue {
  id: string;
  text: string;
  source?: string;
}

export async function getPriorities(params: {
  issueAri: string;
  projectId: string;
  intl: IntlShape;
}): Promise<PriorityItem[]> {
  try {
    const response = await executeObjectResolverAction({
      integrationKey: 'jira',
      actionKey: 'atlassian:work-item:get:priorities',
      parameters: {
        inputs: { projectId: params.projectId },
        target: { ari: params.issueAri },
      },
    } as ExecuteActionRequest);

    const entities: JiraPriorityEntity[] =
      (response as { entities?: JiraPriorityEntity[] })?.entities ?? [];

    return entities.map((entity: JiraPriorityEntity) => ({
      id: entity.id,
      name:
        entity.text ||
        entity.label ||
        params.intl.formatMessage(messages['jira-priority.unknown-priority']),
      iconUrl: entity.source,
      description: entity.description,
    }));
  } catch (error) {
    logError(
      'jira-priority/get:failed',
      error instanceof Error ? error : new Error('jira priority get failed')
    );
    return [];
  }
}

export async function updateIssuePriority(params: {
  priorityId: string;
  issueAri: string;
}): Promise<void> {
  try {
    await executeObjectResolverAction({
      integrationKey: 'jira',
      actionKey: 'atlassian:work-item:update:priority',
      parameters: {
        inputs: { priority: params.priorityId },
        target: { ari: params.issueAri },
      },
    } as ExecuteActionRequest);
  } catch (error) {
    logError(
      'jira-priority/update:failed',
      error instanceof Error ? error : new Error('jira priority update failed')
    );
    throw new Error('priority update failed');
  }
}
